import {
  ApiCustomer,
  ApiLineItem,
  ApiProduct,
  ApiProductImage,
  ApiSale,
  ApiUser,
} from '@vend/types'
import { CustomersStore } from '../../../../factories/database/customers-store'
import { UsersStore } from '../../../../factories/database/users-store'
import { SaleTransformer } from '../../../../factories/sale-transformer'

export interface FailedProductLoad {
  id: string
  hasFailedToLoad: true
}

interface HydratedLineItem extends ApiLineItem {
  product: ApiProduct | FailedProductLoad
}

export interface HydratedSale extends Omit<ApiSale, 'line_items'> {
  customer: ApiCustomer | undefined
  line_items: HydratedLineItem[]
  user: ApiUser | undefined
}

interface Stores {
  customersStore: CustomersStore
  usersStore: UsersStore
  saleTransformer: SaleTransformer
}

const getCustomers = (customersStore: CustomersStore, sales: ApiSale[]) => {
  const customerIds = Array.from(new Set(sales.map(sale => sale.customer_id)))
  const customers: Record<string, ApiCustomer | undefined> = {}
  return Promise.all(
    customerIds.map(id =>
      customersStore.get(id).then(customer => {
        if (customer) {
          customers[id] = customer
        }
      })
    )
  ).then(() => customers)
}

const getVariantParentProductImages = (
  saleTransformer: SaleTransformer,
  variantParentId: string
) =>
  saleTransformer.hydrateProduct(variantParentId).then(product => {
    return (product && product.images) || []
  })

const getProducts = (saleTransformer: SaleTransformer, sales: ApiSale[]) => {
  const productIds = Array.from(
    new Set(sales.flatMap(sale => sale.line_items.map(item => item.product_id)))
  )
  const products: Record<string, ApiProduct | FailedProductLoad> = {}
  return Promise.all(
    productIds.map(id =>
      saleTransformer.hydrateProduct(id).then(product => {
        if (!product) {
          products[id] = {
            id,
            hasFailedToLoad: true,
          }
          return
        }
        // Variant products don't have any product images, these are all stored on the parent product
        if (product?.variant_parent_id) {
          return getVariantParentProductImages(
            saleTransformer,
            product.variant_parent_id
          ).then((parentProductImages: ApiProductImage[]) => {
            product.images = parentProductImages
            products[id] = product
          })
        } else {
          products[id] = product
        }
      })
    )
  ).then(() => products)
}
const getUsers = (usersStore: UsersStore, sales: ApiSale[]) => {
  const userIds = Array.from(new Set(sales.map(sale => sale.user_id)))
  const users: Record<string, ApiUser> = {}
  return Promise.all(
    userIds.map(id =>
      usersStore.get(id).then(user => {
        if (user) {
          users[id] = user
        }
      })
    )
  ).then(() => users)
}

export const isLoadedProduct = (
  product: ApiProduct | FailedProductLoad | undefined
): product is ApiProduct => {
  if (!product) {
    return false
  }

  if ('hasFailedToLoad' in product && product.hasFailedToLoad) {
    return false
  }

  return true
}

export const hydrateSales = (
  sales: ApiSale[],
  { usersStore, customersStore, saleTransformer }: Stores
): Promise<HydratedSale[]> =>
  Promise.all([
    getCustomers(customersStore, sales),
    getProducts(saleTransformer, sales),
    getUsers(usersStore, sales),
  ]).then(([customers, products, users]) => {
    return sales.map(sale => ({
      ...sale,
      customer: customers[sale.customer_id],
      line_items: sale.line_items.map(item => ({
        ...item,
        product: products[item.product_id],
      })),
      user: users[sale.user_id],
      hasFailedToLoad: false,
    }))
  })
